import { Box, Checkbox, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { INTERNAL_PROVIDERS } from '../../../../constants/providers';
import { IngestionSourceType } from '../../../../services/queries/FileIngestionQueries';
import { availableDataTypes, DATA_TYPE_HELPER_TEXT, DATA_TYPE_LABELS, reconcileDataType } from '../data-type';

interface BasicInfoSectionProps {
  isEditMode?: boolean;
}

export function BasicInfoSection({ isEditMode = false }: BasicInfoSectionProps) {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const sourceType = watch('config.sourceType') || 'file';
  const dataTypeOptions = availableDataTypes(sourceType);

  return (
    <Box>
      <Stack spacing={4}>
        <Controller
          name='config.sourceType'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || 'file'}
              onChange={(e) => {
                field.onChange(e.target.value);
                // Clamp the data type when the new source can't run the currently selected
                // one (same rule the form applies on load) — e.g. commentary is supported
                // for file and Snowflake but not API integration.
                const currentDataType = watch('config.dataType');
                if (currentDataType) {
                  const next = reconcileDataType(e.target.value as IngestionSourceType, currentDataType);
                  if (next !== currentDataType) {
                    setValue('config.dataType', next, { shouldDirty: true });
                  }
                }
              }}
              select
              label='Source Type'
              fullWidth
            >
              <MenuItem value='file'>File Upload</MenuItem>
              <MenuItem value='api-integration'>API Integration</MenuItem>
              <MenuItem value='snowflake'>Snowflake</MenuItem>
            </TextField>
          )}
        />

        <Controller
          name='config.dataType'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || 'entity-properties'}
              select
              label='Data Type'
              fullWidth
              helperText={
                DATA_TYPE_HELPER_TEXT[
                  (field.value as keyof typeof DATA_TYPE_HELPER_TEXT) || 'entity-properties'
                ]
              }
            >
              {dataTypeOptions.map((dt) => (
                <MenuItem key={dt} value={dt}>
                  {DATA_TYPE_LABELS[dt]}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {sourceType === 'api-integration' && (
          <Controller
            name='config.integrationProvider'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || ''}
                select
                label='Integration Provider'
                fullWidth
                required
              >
                {INTERNAL_PROVIDERS.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}

        {/*
          `fileType` is the underlying unique slug for every automation config regardless of
          source type — file ingestion calls it "File Type" because that's what it matched
          against incoming filenames historically. For non-file sources it's just an
          identifier, so relabel + reword to fit the picked source type.
        */}
        <TextField
          {...register('fileType', { required: 'Identifier is required' })}
          label={sourceType === 'file' ? 'File Type' : 'Identifier'}
          placeholder={
            sourceType === 'snowflake'
              ? 'e.g., snowflake-custom-fields-sync'
              : sourceType === 'api-integration'
                ? 'e.g., hubspot-companies'
                : 'e.g., client-board-directors'
          }
          fullWidth
          required
          disabled={isEditMode}
          error={!!errors.fileType}
          helperText={
            (errors.fileType?.message as string) ||
            (isEditMode
              ? 'Unique key for this configuration. The key cannot be changed after creation.'
              : 'Unique key for this configuration. Used by the runner to identify the automation.')
          }
        />

        <TextField
          {...register('displayName', { required: 'Display name is required' })}
          label='Display Name'
          placeholder={
            sourceType === 'snowflake'
              ? 'e.g., Snowflake Custom Fields Sync'
              : sourceType === 'api-integration'
                ? 'e.g., HubSpot Companies'
                : 'e.g., Board Directors Import'
          }
          fullWidth
          required
          error={!!errors.displayName}
          helperText={errors.displayName?.message as string}
        />

        <TextField
          {...register('description')}
          label='Description'
          placeholder='Describe what this configuration does'
          fullWidth
          multiline
          rows={3}
        />

        <Controller
          name='active'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label={
                sourceType === 'snowflake'
                  ? 'Active (configuration will run on the daily Snowflake sync)'
                  : sourceType === 'api-integration'
                    ? 'Active (configuration will run on the integration sync)'
                    : 'Active (configuration will be used for file detection)'
              }
            />
          )}
        />
      </Stack>
    </Box>
  );
}
